import React, { useRef, useMemo, useCallback, useEffect } from "react";
import { Box } from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  type ChartOptions,
  type ChartData,
  type Plugin,
  type ScriptableContext,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GoalTargetTrendChartProps {
  /** Week labels for the x-axis, e.g. ["W10", "W11", "W12", "W14", "W15"] */
  labels: string[];
  /** Data values corresponding to each label */
  data: (number | null)[];
  /**
   * Index from which the filled area begins (projection/target zone).
   * Data points before this index render as a plain line; from this index
   * onward the area below the line is filled with a green gradient.
   * Defaults to 0 (fill from the start).
   */
  fillStartIndex?: number;
  /** Optional chart height — defaults to "100%" to fill the parent slot */
  height?: number | string;
  /** Optional override for the line and fill color (defaults to theme green) */
  color?: string;
  /**
   * Total number of targets under the goal — used as the y-axis max so that a
   * flat-at-zero line (no targets mastered yet) renders in the correct context
   * rather than being auto-scaled to fill the chart area.
   */
  totalTargets?: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Creates a vertical gradient from a semi-transparent green at the top
 * to nearly transparent at the bottom.
 */
function createAreaGradient(
  ctx: CanvasRenderingContext2D,
  chartArea: { top: number; bottom: number },
  color: string
): CanvasGradient {
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, `${color}40`); // ~25% opacity
  gradient.addColorStop(1, `${color}08`); // ~3% opacity
  return gradient;
}

const tickColor = "#5B6172";

// ── Base chart options — minimal sparkline style ──
// Dynamic per-render options (y-axis max/min) are merged inside the component.
const BASE_OPTIONS: ChartOptions<"line"> = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: "index" as const,
    intersect: false,
  },
  layout: {
    padding: { top: 4, right: 0, bottom: 0, left: 0 },
  },
  scales: {
    x: {
      display: true,
      grid: { display: false },
      border: { display: false },
      ticks: {
        color: tickColor,
        font: { size: 9, family: "Inter, sans-serif" },
        padding: 4,
        maxRotation: 0,
      },
    },
    y: {
      display: false,
      grid: { display: false },
      border: { display: false },
      beginAtZero: true,
    },
  },
  animation: false,
  plugins: {
    legend: { display: false },
    tooltip: { enabled: false },
  },
};

// ── Custom plugin: draws small tick marks above each x-axis label ──
const tickMarkPlugin: Plugin<"line"> = {
  id: "goalTrendTickMarks",
  afterDraw(chart) {
    const { ctx, scales } = chart;
    const xScale = scales.x;
    if (!xScale) return;

    const y = xScale.top;
    const tickHeight = 3;

    ctx.save();
    ctx.strokeStyle = tickColor;
    ctx.lineWidth = 1;

    xScale.ticks.forEach((_, index) => {
      const x = xScale.getPixelForTick(index);
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, y + tickHeight);
      ctx.stroke();
    });

    ctx.restore();
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Sparkline-style trend chart for Goal cards.
 *
 * Renders a green line across all data points with an optional filled
 * area starting at `fillStartIndex`. Designed to be passed into
 * `GoalCard`'s `chartSlot` prop.
 *
 * @example
 * ```tsx
 * <GoalCard
 *   chartSlot={
 *     <GoalTargetTrendChart
 *       labels={["W10", "W11", "W12", "W14", "W15"]}
 *       data={[80, 80, 80, 85, 90]}
 *       fillStartIndex={2}
 *     />
 *   }
 *   {...otherProps}
 * />
 * ```
 */
const GoalTargetTrendChart: React.FC<GoalTargetTrendChartProps> = ({
  labels,
  data,
  fillStartIndex = 0,
  height = "100%",
  color,
  totalTargets,
}) => {
  const chartRef = useRef<ChartJS<"line"> | null>(null);

  // Cleanup the Chart instance on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      chartRef.current?.destroy();
    };
  }, []);

  // Build per-render options — apply y-axis max when totalTargets is provided
  // so that a flat-at-zero line renders in context rather than filling the chart.
  const optionsChart = useMemo((): ChartOptions<"line"> => {
    if (totalTargets == null) return BASE_OPTIONS;
    return {
      ...BASE_OPTIONS,
      scales: {
        ...BASE_OPTIONS.scales,
        y: {
          ...BASE_OPTIONS.scales!.y,
          max: Math.max(totalTargets, 1),
        },
      },
    };
  }, [totalTargets]);

  const lineColor = color ?? "#4CAF50";
  const fillColor = color ?? "#4CAF50";

  // ── Build the filled-area dataset ──
  // Points before fillStartIndex are null so the Filler plugin skips them.
  const fillData = useMemo(
    () => data.map((value, i) => (i >= fillStartIndex ? value : null)),
    [data, fillStartIndex]
  );

  // ── Dynamic gradient background ──
  const getBackgroundColor = useCallback(
    (context: ScriptableContext<"line">) => {
      const { chart } = context;
      const { ctx, chartArea } = chart;
      if (!chartArea) return "transparent";
      return createAreaGradient(ctx, chartArea, fillColor);
    },
    [fillColor]
  );

  // ── Chart data ──
  const chartData: ChartData<"line"> = useMemo(
    () => ({
      labels,
      datasets: [
        // Layer 1 — The trend line (always visible, no fill)
        {
          data,
          borderColor: lineColor,
          borderWidth: 2,
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0,
          order: 1,
          stepped: true,
        },
        // Layer 2 — Filled area (only from fillStartIndex onward)
        {
          data: fillData,
          borderColor: "transparent",
          borderWidth: 0,
          backgroundColor: getBackgroundColor as any,
          fill: "origin",
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0,
          spanGaps: false,
          order: 2,
          stepped: true,
        },
      ],
    }),
    [labels, data, fillData, lineColor, getBackgroundColor]
  );

  return (
    <Box
      sx={{
        width: "100%",
        height,
        minHeight: 100,
        position: "relative",
      }}
    >
      <Line ref={chartRef} data={chartData} options={optionsChart} plugins={[tickMarkPlugin]} />
    </Box>
  );
};

export default GoalTargetTrendChart;
